import { useRef } from 'react';
import { isPointerOverPortaledOverlayFromEvent } from './utils/overlayPointerGuard';
import { clearFlowchartTextSelections } from './utils/clearFlowchartTextSelections';

/**
 * useFastPan Hook
 * 
 * PERFORMANCE-CRITICAL: Handles canvas panning using direct DOM manipulation
 * to bypass React re-renders. Uses refs exclusively for zero-latency response.
 * 
 * Features:
 * - Rubber-band effect when panning past bounds
 * - RAF throttling for smooth 60fps updates
 * - Instant response on first move
 * - Animated snap-back when released out of bounds
 */
export function useFastPan({
  containerRef,
  panHook,
  setPanAnimated,
  constrainPan,
  cancelPanCorrection,
  isPanningRef,
  zoomRef,
  clearSelection,
}) {
  const { stopPanning } = panHook;
  
  // Track pan start position for detecting click vs drag
  const panStartPosRef = useRef({ x: 0, y: 0 });
  const didPanRef = useRef(false);
  /** Movement below this = click (deselect). At/above = pan. */
  const PAN_THRESHOLD = 8;
  
  // Track if we're in a "fast pan" mode using only refs (no React state)
  const fastPanActiveRef = useRef(false);
  const fastPanStartRef = useRef({ x: 0, y: 0 });
  
  // Track current pan during drag for direct DOM updates
  // Use panHook.panRef (the ref) instead of panHook.pan (React state) to avoid stale values
  const livePanRef = useRef(panHook.panRef?.current || panHook.pan);
  
  // Cache DOM element references
  const canvasRef = useRef(null);
  
  // RAF throttling for updates
  const rafIdRef = useRef(null);
  const pendingPanRef = useRef(null);
  const hasMovedRef = useRef(false); // Track if first move happened (for instant response)
  
  // Cache container ref for use in static handlers
  const containerRefStatic = useRef(containerRef);
  containerRefStatic.current = containerRef;

  // Refs for callbacks that need to be called from static handlers
  const setPanAnimatedRef = useRef(setPanAnimated);
  setPanAnimatedRef.current = setPanAnimated;
  const constrainPanRef = useRef(constrainPan);
  constrainPanRef.current = constrainPan;
  const cancelPanCorrectionRef = useRef(cancelPanCorrection);
  cancelPanCorrectionRef.current = cancelPanCorrection;
  const isPanningRefStatic = useRef(isPanningRef);
  isPanningRefStatic.current = isPanningRef;
  const clearSelectionRef = useRef(clearSelection);
  clearSelectionRef.current = clearSelection;
  const stopPanningRef = useRef(stopPanning);
  stopPanningRef.current = stopPanning;

  // Pan only engages after crossing PAN_THRESHOLD (avoids accidental pan on click-to-deselect)
  const panEngagedRef = useRef(false);
  const panStartPanRef = useRef({ x: 0, y: 0 });
  
  // Store panHook.panRef so static handlers can update it during drag
  const panHookPanRefStatic = useRef(panHook.panRef);
  panHookPanRefStatic.current = panHook.panRef;

  // Sync livePanRef from panHook.panRef when not actively panning
  // This ensures we have the correct pan position even after other operations update it
  const syncLivePan = () => {
    if (!fastPanActiveRef.current && panHook.panRef) {
      livePanRef.current = panHook.panRef.current;
    }
  };

  // Static pan mouse move handler - created ONCE, never recreated
  const fastPanMouseMoveRef = useRef((e) => {
    if (!fastPanActiveRef.current) return;

    if (isPointerOverPortaledOverlayFromEvent(e)) return;

    const dx = e.clientX - panStartPosRef.current.x;
    const dy = e.clientY - panStartPosRef.current.y;

    // Below threshold: treat as a pending click — don't move the canvas yet
    if (!panEngagedRef.current) {
      if (Math.abs(dx) <= PAN_THRESHOLD && Math.abs(dy) <= PAN_THRESHOLD) {
        return;
      }
      panEngagedRef.current = true;
      didPanRef.current = true;
      if (isPanningRefStatic.current) isPanningRefStatic.current.current = true;
      containerRefStatic.current?.current?.classList.add('flowchart--panning');
      window.dispatchEvent(new CustomEvent('flowchartpanstart'));
    }
    
    // Calculate new pan position
    const rawX = e.clientX - fastPanStartRef.current.x;
    const rawY = e.clientY - fastPanStartRef.current.y;
    
    // Apply "rubber band" effect: allow going past bounds but with resistance
    const constrained = constrainPanRef.current 
      ? constrainPanRef.current({ x: rawX, y: rawY }, zoomRef.current)
      : { x: rawX, y: rawY };
    
    // Calculate overshoot and apply resistance (like iOS rubber-banding)
    const RUBBER_BAND_FACTOR = 0.3;
    const overshootX = rawX - constrained.x;
    const overshootY = rawY - constrained.y;
    
    const smoothedPan = {
      x: constrained.x + overshootX * RUBBER_BAND_FACTOR,
      y: constrained.y + overshootY * RUBBER_BAND_FACTOR,
    };
    
    // Store for later sync
    livePanRef.current = smoothedPan;
    
    // Also update panHook.panRef so wheel zoom reads correct position
    if (panHookPanRefStatic.current) {
      panHookPanRefStatic.current.current = smoothedPan;
    }
    
    // Store pending pan update
    pendingPanRef.current = smoothedPan;
    
    // Update DOM immediately on FIRST engaged move, then RAF for subsequent updates
    if (!hasMovedRef.current) {
      hasMovedRef.current = true;
      const canvas = canvasRef.current || (canvasRef.current = containerRefStatic.current?.current?.querySelector('.flowchart__canvas'));
      if (canvas) {
        canvas.style.transform = `translate(${smoothedPan.x}px, ${smoothedPan.y}px) scale(${zoomRef.current})`;
        canvas.style.setProperty('--inverse-zoom', 1 / zoomRef.current);
        canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: smoothedPan.x, y: smoothedPan.y, zoom: zoomRef.current } }));
      }
    } else if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        if (pendingPanRef.current) {
          const { x: px, y: py } = pendingPanRef.current;
          const canvas = canvasRef.current || (canvasRef.current = containerRefStatic.current?.current?.querySelector('.flowchart__canvas'));
          if (canvas) {
            canvas.style.transform = `translate(${px}px, ${py}px) scale(${zoomRef.current})`;
            canvas.style.setProperty('--inverse-zoom', 1 / zoomRef.current);
            canvas.dispatchEvent(new CustomEvent('panupdate', { detail: { x: px, y: py, zoom: zoomRef.current } }));
          }
        }
      });
    }
  });
  
  // Store the actual mouseup handler in a separate ref that we update
  const mouseUpHandlerRef = useRef(null);
  
  // Stable wrapper that calls the current handler
  const fastPanMouseUpRef = useRef((e) => {
    if (mouseUpHandlerRef.current) {
      mouseUpHandlerRef.current(e);
    }
  });
  
  // Update the actual handler (this runs every render but that's OK)
  mouseUpHandlerRef.current = (e) => {
    if (!fastPanActiveRef.current) return;
    
    // Remove listeners immediately
    document.removeEventListener('mousemove', fastPanMouseMoveRef.current);
    document.removeEventListener('mouseup', fastPanMouseUpRef.current);
    
    // Cancel any pending RAF
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    
    // Apply final position immediately
    if (pendingPanRef.current) {
      const { x, y } = pendingPanRef.current;
      const canvas = canvasRef.current || (canvasRef.current = containerRefStatic.current?.current?.querySelector('.flowchart__canvas'));
      if (canvas) {
        canvas.style.transform = `translate(${x}px, ${y}px) scale(${zoomRef.current})`;
        canvas.style.setProperty('--inverse-zoom', 1 / zoomRef.current);
      }
      pendingPanRef.current = null;
    }
    
    const wasClick = !panEngagedRef.current;

    fastPanActiveRef.current = false;
    panEngagedRef.current = false;
    // Clear shared panning ref to re-enable zoom
    if (isPanningRefStatic.current) isPanningRefStatic.current.current = false;
    
    // Remove panning class directly
    containerRefStatic.current?.current?.classList.remove('flowchart--panning');
    
    if (wasClick) {
      // Click on empty canvas — deselect (including instant DOM cleanup)
      if (!e.shiftKey) {
        clearSelectionRef.current?.();
      }
      // Restore canvas transform in case sub-threshold jitter moved it
      const startPan = panStartPanRef.current;
      livePanRef.current = startPan;
      if (panHookPanRefStatic.current) {
        panHookPanRefStatic.current.current = startPan;
      }
      const canvas = canvasRef.current || containerRefStatic.current?.current?.querySelector('.flowchart__canvas');
      if (canvas) {
        canvas.style.transform = `translate(${startPan.x}px, ${startPan.y}px) scale(${zoomRef.current})`;
        canvas.style.setProperty('--inverse-zoom', 1 / zoomRef.current);
      }
    } else if (didPanRef.current) {
      // Committed pan — sync React state (animated snap-back if out of bounds)
      setPanAnimatedRef.current(livePanRef.current);
      window.dispatchEvent(new CustomEvent('flowchartpanend'));
    }
    
    didPanRef.current = false;
    stopPanningRef.current();
  };

  /**
   * Start fast pan mode
   * @param {MouseEvent} e - The mouse event
   */
  const startFastPan = (e) => {
    // CRITICAL: Block all interactions during animations
    const blocker = document.querySelector('.flowchart__animation-blocker');
    if (blocker && blocker.style.display === 'block') {
      return;
    }
    
    // Cancel any pending zoom pan correction to prevent conflicts
    cancelPanCorrectionRef.current?.();

    clearFlowchartTextSelections();
    
    // Sync livePanRef from panHook.panRef BEFORE starting pan
    if (panHook.panRef) {
      livePanRef.current = { ...panHook.panRef.current };
    }
    
    // Listen for move/up; pan only engages after PAN_THRESHOLD (see mousemove)
    fastPanActiveRef.current = true;
    hasMovedRef.current = false;
    panEngagedRef.current = false;
    panStartPanRef.current = { ...livePanRef.current };
    fastPanStartRef.current = {
      x: e.clientX - livePanRef.current.x,
      y: e.clientY - livePanRef.current.y,
    };
    panStartPosRef.current = { x: e.clientX, y: e.clientY };
    didPanRef.current = false;
    
    // Attach native listeners IMMEDIATELY
    document.addEventListener('mousemove', fastPanMouseMoveRef.current);
    document.addEventListener('mouseup', fastPanMouseUpRef.current);
  };

  return {
    fastPanActiveRef,
    syncLivePan,
    startFastPan,
  };
}
